import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalShellProps {
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  /**
   * When true, clicking the backdrop dismisses the modal. Defaults to true to
   * preserve existing behavior. Set to false for form modals where an
   * accidental outside click would discard entered data (industry standard).
   */
  closeOnBackdrop?: boolean;
  /**
   * When true (default), pressing Escape dismisses the modal — the standard
   * keyboard affordance for dialogs. A deliberate keypress, so it stays enabled
   * even for form modals that disable backdrop-dismiss; set false to opt out.
   */
  closeOnEsc?: boolean;
}

export function ModalShell({
  onClose,
  className = '',
  children,
  closeOnBackdrop = true,
  closeOnEsc = true,
}: ModalShellProps) {
  useEffect(() => {
    if (!closeOnEsc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeOnEsc, onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`w-full rounded-2xl bg-surface shadow-modal ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
